import { IHelpdeskNewTicketValues, IHelpdeskReplyTicketValue } from "@kuika/kuika-cl-model";
import { PartialContextProvider } from "../../../shared/patial/partial-context-provider";
import { IScreenDesignContextState } from "../screen-design-context";
import { IScreenDesignContextProviderProps, ScreenDesignContextProvider } from "../screen-design-context-provider";

/** Which view the Helpdesk modal is showing: the ticket list, or the status board. */
export type HelpdeskModalView = "inbox" | "board";

export interface HelpdeskProviderState {
  /**
   * Survives closing the modal — same storage lifetime as the half-written ticket — but the
   * value only matters while Helpdesk is open: `openHelpdesk` forces this back to "inbox" on
   * every OPEN, so a tab left on Board is never what the next open shows. It still updates
   * live while the panel stays open (the tab switcher writes it directly), which is what lets
   * a Board→Inbox switch remount the ticket canvas without `openHelpdesk` running again.
   */
  helpdeskModalView: HelpdeskModalView;
  setHelpdeskModalView: (view: HelpdeskModalView) => void;
  tempHelpdeskNewTicketValues: IHelpdeskNewTicketValues;
  setTempHelpdeskNewTicketValues: (values: IHelpdeskNewTicketValues) => void;
  tempHelpdeskReplyTicketValues: IHelpdeskReplyTicketValue[];
  updateTempHelpdeskReplyTicketValue: (value: IHelpdeskReplyTicketValue) => void;
  deleteTempHelpdeskReplyTicketValue: (id: string) => void;
  /**
   * Resets Helpdesk to its landing state for one OPEN: Inbox tab, nothing selected, composer
   * open. Call it once per open — from the modal's mount and from `DesignSituationProvider`
   * when the full-page view is entered — never from `ZendeskCanvas.componentDidMount`, which
   * also remounts on every Board→Inbox tab switch inside an already-open modal and must leave
   * whatever ticket that switch just selected alone. Deliberately does not touch the draft: a
   * half-written ticket is meant to still be there when the composer reopens.
   */
  openHelpdesk: () => void;
}

export class HelpdeskProvider extends PartialContextProvider<
  ScreenDesignContextProvider,
  IScreenDesignContextProviderProps,
  IScreenDesignContextState,
  HelpdeskProviderState
> {
  static defaultState: HelpdeskProviderState = {
    helpdeskModalView: "inbox",
    setHelpdeskModalView: () => {},
    tempHelpdeskNewTicketValues: {} as IHelpdeskNewTicketValues,
    setTempHelpdeskNewTicketValues: () => {},
    tempHelpdeskReplyTicketValues: [],
    updateTempHelpdeskReplyTicketValue: () => {},
    deleteTempHelpdeskReplyTicketValue: () => {},
    openHelpdesk: () => {}
  };

  getProviderValues = (): HelpdeskProviderState => {
    return {
      helpdeskModalView: this.state.helpdeskModalView,
      setHelpdeskModalView: this.setHelpdeskModalView,
      tempHelpdeskNewTicketValues: this.state.tempHelpdeskNewTicketValues,
      setTempHelpdeskNewTicketValues: this.setTempHelpdeskNewTicketValues,
      tempHelpdeskReplyTicketValues: this.state.tempHelpdeskReplyTicketValues,
      updateTempHelpdeskReplyTicketValue: this.updateTempHelpdeskReplyTicketValue,
      deleteTempHelpdeskReplyTicketValue: this.deleteTempHelpdeskReplyTicketValue,
      openHelpdesk: this.openHelpdesk
    };
  };

  setHelpdeskModalView = (view: HelpdeskModalView): void => {
    this.setState({ helpdeskModalView: view });
  };

  /** See the docstring on `HelpdeskProviderState.openHelpdesk`. */
  openHelpdesk = (): void => {
    this.setState({ helpdeskModalView: "inbox" });
    this.partial.zendeskProvider.onZendeskTicketSelect(null);
    this.partial.zendeskProvider.onZendeskTicketCreate(true);
  };

  setTempHelpdeskNewTicketValues = (values: IHelpdeskNewTicketValues) => {
    this.setState({ tempHelpdeskNewTicketValues: values });
  };

  updateTempHelpdeskReplyTicketValue = (values: IHelpdeskReplyTicketValue) => {
    const { tempHelpdeskReplyTicketValues } = this.state;
    if (tempHelpdeskReplyTicketValues) {
      const index = tempHelpdeskReplyTicketValues.findIndex((x) => x.ticketId === values.ticketId);
      if (index !== -1) {
        tempHelpdeskReplyTicketValues[index] = values;
      } else {
        tempHelpdeskReplyTicketValues.push(values);
      }
      this.setState({ tempHelpdeskReplyTicketValues });
    }
  };

  deleteTempHelpdeskReplyTicketValue = (ticketId: string) => {
    const { tempHelpdeskReplyTicketValues } = this.state;
    if (tempHelpdeskReplyTicketValues) {
      const index = tempHelpdeskReplyTicketValues.findIndex((x) => x.ticketId === ticketId);
      if (index !== -1) {
        tempHelpdeskReplyTicketValues.splice(index, 1);
        this.setState({ tempHelpdeskReplyTicketValues });
      }
    }
  };
}
